/**
 * Das Handbuch -- der Wortlaut (#92).
 *
 * **Eine eigene Datei neben `de.ts`, und aus demselben Grund wie dort:** ein
 * Mensch liest und ändert sie. Sie trägt Sätze, kein Warum -- die Begründungen
 * stehen an dem Code, den sie erklären. Die drei Regeln aus `de.ts` gelten
 * hier wörtlich: ein Satz sagt, was etwas tut, kein Gedankenstrich, Anrede
 * „du".
 *
 * **Und sie stand einmal gegen ihre eigene Regel** (1.55.0). Aus dem Betrieb:
 * „weniger Erklärungen, warum etwas so ist, sondern einfache Erklärungen, wie
 * es zu bedienen ist." Siebzehn Sätze erklärten Server, Apple oder
 * Verarbeitung, und zwei Absätze waren zu Aufzählungen angewachsen. Jeder
 * Absatz nennt jetzt einen Weg: wo es steht, was du antippst, was danach
 * passiert. Wer wissen will, warum etwas so gebaut ist, liest den Code oder
 * die Datenschutzseite.
 *
 * **Gegliedert nach dem, was jemand darf** (`fuer`), nicht nach Kapiteln:
 * ein Mitlesender liest die Abschnitte für alle, ein Autor dazu seine, die
 * Verwaltung ihre, der Supervisor seine. Wer die Seite aufmacht, sieht genau
 * das, was er auch bedienen kann -- aus dem Issue: „immer angepasst an die
 * jeweiligen Rechte."
 *
 * **Und ein Abschnitt über einen Dienst steht nur, wenn der Dienst da ist**
 * (`dienst`): ohne Apple-Zugang gibt es kein Wetter zu erklären, ohne Giphy
 * keine GIF-Suche. Dieselbe Regel wie auf der Datenschutzseite -- die Seite
 * sagt die Wahrheit über diese Installation, nicht über die Software.
 */
export type HandbuchFuer = 'alle' | 'autor' | 'verwaltung' | 'betrieb'

export interface HandbuchAbschnitt {
  /** Steht am Kasten (`data-abschnitt`), damit ein Prüflauf ihn findet. */
  schluessel: string
  fuer: HandbuchFuer
  /** Nur zeigen, wenn dieser Dienst eingerichtet ist. */
  dienst?: 'weather' | 'gif' | 'place'
  titel: string
  absaetze: string[]
}

export const handbuch = {
  titel: 'Dokumentation',
  lead: 'Was du hier tun kannst und wo du es findest.',
  gruppen: {
    alle: 'Für alle',
    autor: 'Für Autorinnen und Autoren',
    verwaltung: 'Für die Verwaltung',
    betrieb: 'Für den Supervisor',
  } satisfies Record<HandbuchFuer, string>,

  abschnitte: [
    // ------------------------------------------------------------ alle
    {
      schluessel: 'anmelden',
      fuer: 'alle',
      titel: 'Anmelden',
      absaetze: [
        'Du meldest dich mit deiner E-Mail-Adresse und deinem Passwort an. Ein Konto bekommst du über eine Einladung, als Code oder als Link.',
        'Muss der Autor oder die Autorin deinen Beitritt erst bestätigen, siehst du eine Tafel mit deiner Anfrage. Du kannst die App dann schließen. Ist die Bestätigung da, bekommst du eine E-Mail mit einem Link in die Reise.',
        'Hast du dein Passwort vergessen, forderst du auf der Anmeldeseite einen Link per E-Mail an.',
        'Unter Einstellungen › Konto legst du einen Passkey an. Danach genügt Face ID, Touch ID oder der Code deines Geräts. Dein Passwort gilt weiter.',
      ],
    },
    {
      schluessel: 'appAnlegen',
      fuer: 'alle',
      titel: 'Die App auf den Startbildschirm legen',
      absaetze: [
        'Über der Reiseliste steht „Travel Broadcast als App hinzufügen“. Tippe darauf, dann führt dich die App weiter. Dieselbe Karte findest du unter Einstellungen › Benachrichtigungen.',
        'Am iPhone und am iPad: Öffne die Seite in Safari und such das Teilen-Symbol, einen Pfeil aus einem Kasten. Es steht neben der Adressleiste oder im Menü darin. Wähle „Zum Home-Bildschirm“ und bestätige mit „Hinzufügen“. In Chrome, Firefox oder Edge geht es nicht.',
        'Auf Android: Öffne die Seite in Chrome, tippe oben rechts auf die drei Punkte und wähle „App installieren“. Steht das nicht da, nimm „Zum Startbildschirm hinzufügen“.',
        'Starte danach über das neue Symbol. Erst dann kannst du Benachrichtigungen einschalten, und die Leisten des Browsers sind weg.',
      ],
    },
    {
      schluessel: 'orientierung',
      fuer: 'alle',
      titel: 'Wo was steht',
      absaetze: [
        'Auf größeren Bildschirmen steht links die Liste der Reisen, daneben der Inhalt. Auf Smartphones wird nur eines von beiden angezeigt. Der Koffer oben links bringt dich zur Liste zurück.',
        'In einer Reise schaltest du oben zwischen „Chat“ und „Karte“ um. Hat die Reise eine Playlist, steht daneben eine Note. Das Info-Symbol rechts öffnet die Reiseseite.',
        'Das Zahnrad über der Liste öffnet die Einstellungen. Dort stehen dein Konto, die Benachrichtigungen, die Darstellung und diese Seite.',
        'Auf der Reiseseite führt „Fotos“ zu allen Fotos und Videos der Reise, nach dem Tag der Aufnahme geordnet, in der Uhrzeit am Ort. Fotos aus Antworten stehen dort nicht, nur im Chat.',
        '„Abstimmungen“ auf der Reiseseite zeigt alle Abstimmungen der Reise, die neueste oben. Du stimmst dort ab wie im Chat. „Springe zu“ führt zu einer bestimmten Abstimmung, „Im Chat zeigen“ zu dem Beitrag, in dem sie gestellt wurde.',
      ],
    },
    {
      schluessel: 'mitlesen',
      fuer: 'alle',
      titel: 'Mitlesen und antworten',
      absaetze: [
        'Ein Tipp auf eine Reise öffnet den Chat. Die Beiträge der Autorin oder des Autors stehen links, deine Antworten rechts.',
        'Was du schreibst, sieht nur die Autorin oder der Autor. Niemand sonst liest deine Antworten, sieht deine Reaktionen oder erfährt, wie du abgestimmt hast.',
        'Du antwortest mit Text, Emojis und Sprachnachrichten. Auf einen Beitrag oder ein Foto reagierst du mit einem Herz.',
        'Drücke lange auf einen Beitrag, dann öffnet sich ein Menü: antworten, reagieren, ein Foto sichern.',
        'Bei einer Abstimmung tippst du auf deine Antwort. Das Abstimmergebnis wird dann angezeigt.',
        'Gliedert der Autor / die Autorin den Bericht nach Reisetagen, steht über einem Beitrag eine Zeile mit Datum, „Tag 3“ und Wochentag. Dann steht in der Pille oben ein Kalender: ein Tipp darauf zeigt alle Tage, ein Tipp auf einen Tag springt im Chat dorthin.',
        'Eine Adresse im Text wird ein Link. Ein Tipp darauf öffnet zuerst ein Blatt: dort steht, wohin der Link führt, und eine Warnung, wenn an der Adresse etwas auffällt. Erst „Im Browser öffnen“ öffnet sie, und zwar im Browser, nicht in der App. Ob eine Seite gefährlich ist, prüft die App nicht bei einem fremden Dienst. Sieh dir das Ziel an, bevor du tippst.',
        'Mit dem Trichter oben rechts blendest du einen Teil des Chats aus: „Nur Beiträge an alle“ oder „Nur private Nachrichten“. Unter dem Titel steht dann, was du siehst, und wie viele neue Nachrichten ausgeblendet sind. Die Wahl gilt für diese Reise auf diesem Gerät. Tippst du auf eine Benachrichtigung zu einer ausgeblendeten Nachricht, zeigt der Chat wieder alles. Die Zahl in der Reiseliste zählt alle Nachrichten, auch die ausgeblendeten, bis du den Chat öffnest.',
      ],
    },
    {
      schluessel: 'konto',
      fuer: 'alle',
      titel: 'Dein Konto',
      absaetze: [
        'Unter Einstellungen › Konto änderst du deinen Namen. Er steht über deinen Nachrichten.',
        'Dort setzt du auch ein neues Passwort und legst einen Passkey an oder nimmst ihn wieder weg.',
      ],
    },
    {
      schluessel: 'gifs',
      fuer: 'alle',
      dienst: 'gif',
      titel: 'GIFs',
      absaetze: [
        'Hinter dem Plus in der Eingabezeile steht „GIFs suchen“. Über dem Feld stehen acht Vorschläge, ein Tipp darauf reicht.',
        'Das gewählte GIF wird dann im Chat wie ein Bild angezeigt.',
      ],
    },
    {
      schluessel: 'karte',
      fuer: 'alle',
      titel: 'Die Karte',
      absaetze: [
        'Der Reiter „Karte“ zeigt die Route und die freigegebenen POIs. Unter der Karte stehen die Kärtchen, der neueste POI ganz links.',
        'Die Karte öffnet in der Gegend des neuesten POIs. Der Rahmen-Knopf links zeigt die ganze Reise; liegt die Reise in mehreren Gegenden, führt der Knopf darunter in die Gegend des neuesten POIs zurück.',
        'Wische durch die Kärtchen, dann rückt die Karte den POI in die Mitte. Ein Tipp zeigt weitere Details wie Fotos, Videos und Spots.',
        'Tippe ein Foto an, dann wird es in der Galerie groß angezeigt. Wische zum nächsten. Ein Foto ziehst du mit zwei Fingern auf und sicherst es auf dein Gerät; bei einem Video wische neben dem Player, sonst spulst du.',
      ],
    },
    {
      schluessel: 'playlist',
      fuer: 'alle',
      titel: 'Die Playlist',
      absaetze: [
        'Hat die Reise eine Playlist, steht in der Pille ein Reiter mit einer Note. Dort steht die Musik der Reise, neueste zuerst.',
        'Jeder Eintrag zeigt den Satz des Autors / der Autorin als Überschrift, darunter den Player von Spotify oder Apple Music mit der ganzen Liste und einen Knopf, der das Stück in der App des Anbieters öffnet.',
        'Ohne Anmeldung spielt der Player nur einen kurzen Ausschnitt. Hast du ein Apple-Music-Abo, melde dich im Apple-Player an und tipp danach einmal „Player neu laden“. Dann spielen ganze Titel.',
        'Musik hinzufügen darf nur der Autor / die Autorin. Antworten gibt es hier nicht.',
        'Kommt ein Stück dazu, bekommst du eine Benachrichtigung, wenn du sie eingeschaltet hast. Der Tipp darauf führt auf diese Seite.',
        'Die Seite gibt es nur online, denn sie holt die Player von Spotify und Apple Music. Die beiden erfahren so, dass jemand zusieht. Mehr dazu unter Einstellungen › Datenschutz.',
      ],
    },
    {
      schluessel: 'benachrichtigungen',
      fuer: 'alle',
      titel: 'Benachrichtigungen',
      absaetze: [
        'Unter Einstellungen › Benachrichtigungen schaltest du sie ein. Dein Gerät fragt dabei einmal nach Erlaubnis.',
        'Du kannst dir eine Testnachricht senden, um die Funktion zu prüfen.',
        'Auf der Reiseseite stellst du eine einzelne Reise stumm. Die Zahl am App-Symbol zählt, was du noch nicht gelesen hast.',
        'Es klingelt bei neuen Beiträgen, bei Antworten und bei neuer Musik in der Playlist. Musik in der Playlist zählt die Zahl am App-Symbol nicht mit: sie steht in der Sammlung und nicht im Chat.',
        'Ist die App gerade offen, erscheint oben ein eigenes Banner. Tippe darauf, dann kommst du zur Nachricht. Bist du schon im richtigen Chat, steht die Nachricht einfach da.',
      ],
    },
    {
      schluessel: 'ohneNetz',
      fuer: 'alle',
      titel: 'Ohne Netz (offline)',
      absaetze: [
        'Die App öffnet sich auch ohne Empfang und zeigt den zuletzt gelesenen Stand. Oben steht dann ein Streifen.',
        'Was du ohne Netz schreibst, steht blass im Verlauf und geht raus, sobald wieder Netz da ist.',
        'Bleibt ein Beitrag hängen, steht an der blassen Blase, warum. Dort versuchst du es noch einmal oder verwirfst ihn.',
        'Ein Video kann im offline Modus nicht hinzugefügt werden. „Aktuelles Wetter“ geht auch ohne Netz: Ort und Zeit sind gemerkt, die Karte kommt, sobald wieder Netz da ist.',
      ],
    },
    {
      schluessel: 'darstellung',
      fuer: 'alle',
      titel: 'Darstellung',
      absaetze: [
        'Unter Einstellungen › Darstellung wählst du die Schriftgröße und die Farben deiner Reiseliste. Beides gilt für dein Konto auf jedem Gerät.',
        'Hell und dunkel folgen deinem Gerät.',
      ],
    },

    // ----------------------------------------------------------- autor
    {
      // Die Streckenstatistik steht auf der Reiseseite, und dorthin kommt
      // nur, wer die Reise führt -- deshalb bei den Autoren und nicht bei
      // allen.
      schluessel: 'statistik',
      fuer: 'autor',
      titel: 'Streckenstatistik',
      absaetze: [
        'Auf der Reiseseite steht die Streckenstatistik: Strecke, Reisezeit, Höhenmeter und die Zahl der Beiträge, Fotos und POIs.',
        'Hinter dem Plus im Chat schickst du die Streckenstatistik eines Tages. Im Blatt stehen die Abschnitte des Tages mit ihrem Fahrzeug. Ein Tipp auf ein anderes Fahrzeug ändert es für die Aufzeichnung.',
        'Ist „Route als Video zeigen“ an, wird aus der Route ein kurzes Video: erst der ganze Tag, dann jeder Abschnitt aus der Nähe mit seinem Fahrzeug, am Ende wieder der ganze Tag. Highlights erscheinen mit ihrem Namen, wenn das Fahrzeug vorbeikommt, und eines mit Foto trägt sein Bild in der Nadel wie auf der Karte. Dort hält das Fahrzeug kurz an, wie lange, stellst du mit den anderen Werten des Videos ein. Größe und Bildrate kommen aus den Einstellungen für Videos: die lange Kante wird die Breite, im Format 16:9. Kann ein Gerät diese Größe nicht rechnen, nimmt es 1280 × 720.',
        'Ohne Video wird die Route ein Bild. Highlights mit Foto stehen darauf mit ihrem Bild in der Nadel, wie auf der Karte und im Video.',
        'Das Video entsteht auf deinem Gerät. Solange es rechnet, steht ein Fenster mit Fortschritt da. Bleib so lange in der App. Brichst du ab oder verlässt du die App, wird nichts gesendet, und du startest neu.',
        'Die Länge des Videos stellst du fest ein oder lässt sie nach der Strecke rechnen. Nach der Strecke fährt jeder Abschnitt 0,2 bis 2 Sekunden je zehn Kilometer (Vorgabe 0,6), mindestens drei Sekunden. Halte und die Flüge der Kamera zwischen den Abschnitten kommen dazu. Wird das Video länger als bei „Höchstens“ (bis 5 Minuten), fragt das Blatt. Die Vorgabe der Reise steht auf der Karte unter Bearbeiten › Darstellung › Mehr, die für neue Reisen in der Verwaltung.',
        'Hat der Tag mehrere Abschnitte, steht jeder auf der Karte in einer eigenen Zeile: Fahrzeug, Uhrzeit, Strecke, Dauer und von wo nach wo. So steht es auch im Buch. Ältere Karten zeigen die Abschnitte, sobald du sie änderst.',
        'Ein langer Abschnitt nimmt im Video viel Platz ein, etwa ein Flug über den Atlantik. Im Blatt der Abschnitte auf der Karte und im Blatt der Streckenstatistik stellst du deshalb je Abschnitt ein Tempo ein, von Zwanzigstel-Tempo über normal bis 500× so schnell. Mit Viertel-Tempo fährt ein Abschnitt viermal so lange wie bei normalem Tempo, mit doppeltem Tempo halb so lange, aber mindestens 3 Sekunden. Das Video wird um diese Zeit länger oder kürzer. Bei fester Länge gilt die Länge für normales Tempo. Wird das Video länger als eingestellt, fragt das Blatt, ob es länger werden darf (bis zum Wert bei „Höchstens“, den du neben der Länge einstellst) oder ob alles gleichmäßig gekürzt wird. Unten im Blatt der Streckenstatistik steht immer, wie lang das Video wird, mit deinem Tempo, deinen Werten und der Zeit zwischen „Von“ und „Bis“.',
        'Unter den Werten des Videos stehen fünf Schalter: „Fahrzeug verfolgen“ (die Kamera fährt mit, nach Westen geht die Fahrt nach links, nach Osten nach rechts), „Gekippt (3D)“ (die Karte liegt schräg wie aus einem Flugzeugfenster), „Höhenprofil“ (unten läuft ein Punkt über die Höhen des Tages), „Gelände“ (Berge und Täler stehen im Bild, die Karte kommt dann von OpenFreeMap) und das Kartenbild. Lässt sich das Gelände nicht laden, kommt das Video ohne. Sind alle aus, ist es das Video wie bisher. Anfang und Ende zeigen den ganzen Tag von oben. Die Streckenstatistik merkt sich alle Werte des Videos: Bei „Neu erstellen“ stehen sie wieder im Blatt. Hat die Aufzeichnung keine Höhen, kommt das Video ohne Höhenprofil.',
        'Liegt „Bis“ vor „Von“, zählt es zum nächsten Tag. So bleibt ein Nachtflug beim Tag, an dem er begann.',
        'Jede Uhrzeit auf der Karte steht in der Zeitzone, die die Reise zu diesem Zeitpunkt hat. Liegt eine Uhrzeit in einer anderen Zeitzone als der Start, steht die Zone dabei, etwa „11:12 (New York)“.',
        'Wechselt die Zeitzone der Reise erst Stunden nach einer Landung oder schon vor dem Abflug, schlägt das Blatt vor, sie ab der Landung gelten zu lassen. Ein Tipp stellt den Eintrag in den Zeitzonen der Reise um.',
      ],
    },
    {
      schluessel: 'reiseAnlegen',
      fuer: 'autor',
      titel: 'Eine Reise anlegen',
      absaetze: [
        'Das Plus über der Reiseliste legt eine Reise an. Du gibst Titel und Zeitraum ein. Die Zeitzone deines Geräts wird die erste Zeitzone der Reise.',
        'Fährst du in eine andere Zeitzone, fragt der Chat: „Ab jetzt für die Reise übernehmen?“ Sagst du Nein, fragt er am nächsten Tag wieder, solange du in dieser Zone bist. Auf der Reiseseite unter „Zeitzonen der Reise“ siehst du alle, mit dem Zeitpunkt, ab dem sie gelten. Dort fügst du eine hinzu, auch nachträglich mit einem früheren Zeitpunkt. Fotos ohne eigene Zeitzone und Halte aus einer Aufzeichnung rücken dann an die richtige Uhrzeit. Sommer- und Winterzeit rechnet die App selbst.',
        'Auf der Reiseseite setzt du ein Symbolbild, einen Chat-Hintergrund und den Willkommensgruß für alle Neuen.',
        'Zum Einladen erzeugst du dort einen Code oder einen Link. Beides ist befristet, und du kannst es zurückziehen.',
        'Willst du jede Anfrage selbst bestätigen, schaltest du das auf der Reiseseite ein. Offene Anfragen stehen dort mit einer Zahl.',
      ],
    },
    {
      schluessel: 'schreiben',
      fuer: 'autor',
      titel: 'Beiträge schreiben',
      absaetze: [
        'Du schreibst Text mit Emojis. Das Mikrofon nimmt eine Sprachnachricht auf.',
        'Hinter dem Plus liegen Fotos und Videos, die Abstimmung, die Streckenstatistik, das Wetter, die Musik und der Tagestrenner.',
        'Unter einer Abstimmung schließt du sie mit „Schließen“. Danach kann niemand mehr abstimmen, und alle sehen das Ergebnis. Hast du sie aus Versehen geschlossen, öffnest du sie mit „Wieder öffnen“. Wer noch nicht abgestimmt hat, sieht das Ergebnis dann wieder erst nach der eigenen Stimme.',
        'Ein Tagestrenner gliedert den Bericht nach Reisetagen: Datum, „Tag 3“ und Wochentag stehen in einer Zeile, dazu eine Erläuterung wie „von New York nach Burlington VT“, wenn du magst. Er ist ein eigener Beitrag ohne Text. Niemand bekommt dafür eine Mitteilung. Im Buch wird daraus ein Kapitel. Auf der Reiseseite wählst du, ob der Zähler „Tag“ oder „Day“ heißt.',
        'Drückst du lange auf einen Tagestrenner, stehen dort „Tagestrenner ändern“ und „Löschen“, wie bei jedem Beitrag.',
        'Was du vergessen hast, fügst du an der richtigen Stelle ein: lange auf einen Beitrag drücken, dann „Beitrag davor einfügen“ oder „Beitrag danach einfügen“. Über der Eingabezeile steht, wo der Beitrag landet. Du schreibst ihn wie jeden anderen, mit allem, was hinter dem Plus liegt, auch einen Tagestrenner. Antworten unter dem gewählten Beitrag bleiben bei ihm.',
        'Ein eingefügter Beitrag steht unter dem Tag des gewählten Beitrags. Niemand bekommt dafür eine Mitteilung, und er zählt nicht als ungelesen. Wer mitliest, findet ihn beim Blättern. Schreibst du ihn an einem anderen Tag, steht in seiner Blase das Datum vor der Uhrzeit.',
        'Fügst du einen Tagestrenner ein, schlägt das Blatt die Nummer vor. Gibt es diese Nummer danach schon, ist „nachfolgende automatisch neu nummerieren“ angehakt. Beides kannst du ändern.',
        'Einfügen geht nur mit Netz. Ohne Netz schreibst du wie immer ans Ende.',
        '„Nur an …“ schickt einen Beitrag an eine einzelne Person. Ihre Antworten stehen in einem eigenen Verlauf unter der Reise.',
        'Im Verlauf mit einer Person blendet der Trichter oben rechts deine Beiträge an alle aus. Dann stehen nur noch eure privaten Nachrichten da.',
        'Drücke lange auf einen eigenen Beitrag, um ihn zu ändern oder zu löschen. Der Haken sagt dir, wie viele ihn gelesen haben.',
        'Beim Ändern stehen unter dem Text auch die Bilder und Videos des Beitrags. Tipp auf das Kreuz, um eines herauszunehmen. Es verschwindet erst mit „Speichern“. Hängt es auch an einem Highlight, einem Spot oder ist es das Reisebild, bleibt es dort. Sonst wird es gelöscht, samt den Reaktionen darauf.',
        'Wer reagiert hat, siehst du mit langem Drücken auf den Beitrag und „Wer hat reagiert?“. Ein langer Druck auf ein Emoji zeigt dasselbe, auch an Fotos, Highlights und Spots.',
      ],
    },
    {
      schluessel: 'wetterSchreiben',
      fuer: 'autor',
      dienst: 'weather',
      titel: 'Wetterkarte',
      absaetze: [
        'Hinter dem Plus stehen „Aktuelles Wetter“ und „Wetter an Ort und Zeit“. Das erste nimmt deinen Standort, das zweite einen gesuchten Ort und eine vergangene Stunde.',
        'Ohne Netz merkt sich die App Ort und Zeit. Die Karte kommt, sobald wieder Netz da ist.',
        'Für eine Stunde ohne Wetter bekommst du einen Satz statt einer Karte.',
      ],
    },
    {
      schluessel: 'musikSchreiben',
      fuer: 'autor',
      titel: 'Musik',
      absaetze: [
        'Hinter dem Plus steht „Musik hinzufügen“. Kopier in Spotify oder Apple Music den Link über „Teilen“ und füge ihn ein. Schreib einen Satz dazu, wenn du magst.',
        'Im Chat steht danach der Player von Spotify oder Apple Music mit der ganzen Liste. Darunter bleibt der Knopf, der das Stück in der App oder im Browser öffnet.',
        'Der Player lädt, sobald die Karte ins Bild kommt. Dabei erfahren Spotify oder Apple, dass jemand zusieht. Mehr dazu unter Einstellungen › Datenschutz.',
        'Ohne Anmeldung spielt der Player einen kurzen Ausschnitt, das ist der Normalfall. Hast du ein Apple-Music-Abo, melde dich im Apple-Player an und tipp danach einmal „Player neu laden“. Dann spielen ganze Titel. Das gilt für jeden einzeln, und jeder entscheidet es selbst.',
        'Passt der Link nicht, sagt die App es dir und schickt nichts. Ohne Netz geht es nicht, denn der Link wird geprüft.',
        'Im Buch steht die Musik als Zeile mit Titelbild, Titel und Interpret, darunter der Link zu Spotify oder Apple Music. Im PDF und im EPUB lässt er sich antippen. Abspielen lässt sich die Musik im Buch nicht.',
      ],
    },
    {
      schluessel: 'kartenlinkSchreiben',
      fuer: 'autor',
      titel: 'Link zur Karte',
      absaetze: [
        'Hinter dem Plus steht „Link zur Karte“. Dort stehen alle freigegebenen POIs, die neuesten oben. Wähle einen oder mehrere und tipp „Hinzufügen“. Im Chat steht dann ein Beitrag mit ihren Kärtchen, wie nach einer Freigabe.',
        'Ein POI, der schon in einem Beitrag steht, trägt eine Sprechblase. Du kannst ihn trotzdem noch einmal wählen, dann steht er in mehreren Beiträgen.',
        'Löschst du einen solchen Beitrag, verschwindet nur dieser Beitrag. Der POI bleibt auf der Karte.',
        'Im Entwurf geht es genauso. Der Beitrag geht dann mit dem Bericht hinaus.',
      ],
    },
    {
      schluessel: 'playlistPflegen',
      fuer: 'autor',
      titel: 'Die Playlist der Reise',
      absaetze: [
        'Auf der Reiseseite schaltest du die Playlist ein. Danach gibt es den Reiter mit der Note, für dich und für alle Mitlesenden.',
        'Auf der Seite steht „Musik hinzufügen“. Es geht derselbe Weg wie im Chat: Link aus Spotify oder Apple Music einfügen, Satz dazu, fertig. Eine ganze Playlist geht genauso wie ein einzelner Titel.',
        'Jedes neue Stück klingelt bei allen Mitlesenden, die Benachrichtigungen eingeschaltet haben. Auf dem Sperrbildschirm steht dein Satz dazu; ohne Satz steht dort, um welche Art Musik es geht.',
        '„Aus der Playlist nehmen“ entfernt einen Eintrag, nach einer Rückfrage. Schaltest du die Playlist wieder aus, bleibt alles erhalten und ist wieder da, sobald du sie einschaltest.',
        'Musik im Chat gibt es weiterhin. Die beiden sind getrennt: im Chat ist ein Lied ein Beitrag im Verlauf, auf der Playlist steht die Sammlung der Reise.',
        'Im Buch bekommt die Playlist ein eigenes Kapitel, hinter den POIs. Jeder Eintrag steht dort mit Titelbild, Titel und deinem Satz dazu.',
      ],
    },
    {
      schluessel: 'entwurf',
      fuer: 'autor',
      titel: 'Der Reisebericht als Entwurf',
      absaetze: [
        'Auf der Reiseseite schaltest du den Entwurf ein. Danach gibt es den Reiter „Reisebericht“.',
        'Was du dort schreibst, siehst nur du. Die Beiträge lassen sich sortieren.',
        'Auch im Entwurf fügst du mit langem Drücken einen Beitrag davor oder danach ein.',
        'Mit „Veröffentlichen“ gehen sie in dieser Reihenfolge ans Ende des Chats, an alle und mit einer Mitteilung. Fotos, Abstimmungen und Wetterkarten wandern mit.',
      ],
    },
    {
      schluessel: 'karteBearbeiten',
      fuer: 'autor',
      titel: 'Die Karte bearbeiten',
      absaetze: [
        'Im Reiter „Bearbeiten“ setzt du POIs. „Highlight“ nimmt einen Tipp auf die Karte, „Hier“ deinen Standort, „Aus Foto/Video“ den Ort einer Aufnahme.',
        'Jeder POI bekommt Titel, Text, Fotos, Videos und Sprachnachrichten.',
        'Ein Foto aus dem Chat legst du mit langem Drücken und „Auf die Karte“ als POI ab. Umgekehrt holt „Aus dieser Reise“ hinter dem Plus in den Chat, was schon an einem POI hängt.',
        'Die Route lädst du als GPX-Datei hoch, zum Beispiel aus myTracks, Komoot, OsmAnd oder Garmin. Aus längeren Aufenthalten macht die App Vorschläge, die du übernimmst oder verwirfst.',
        'Beim Hochladen wählst du das Fahrzeug. Ohne Wahl ist es das Auto.',
        'Fährst du an einem Tag mit mehreren Fahrzeugen, teilst du die Aufzeichnung: Tipp in ihrer Zeile auf die Fahrzeuge. Du teilst nach Uhrzeit oder mit einem Tipp auf die Route, und jeder Abschnitt bekommt sein Fahrzeug. Sieht ein Sprung nach einem Flug aus, schlägt die App „Als Flug teilen“ vor.',
        'Die Trennstellen bleiben beim erneuten Einlesen erhalten. Löschst du die Aufzeichnung, gehen sie mit. „Alle Trennstellen entfernen“ macht wieder einen Abschnitt daraus.',
        'Ein neuer POI ist erst ein Entwurf. Mit „Freigeben“ zeigst du ihn allen und schreibst ihn auf Wunsch in den Chat.',
      ],
    },
    {
      schluessel: 'ortsdienste',
      fuer: 'autor',
      dienst: 'place',
      titel: 'Ort suchen, Spots und POIs verbinden',
      absaetze: [
        '„Ort suchen“ findet einen Ort über seinen Namen und setzt die Nadel dorthin.',
        '„In der Nähe finden“ zeigt Lokale, Museen und mehr rund um einen POI. So sagt ein Highlight, wo genau du warst.',
        '„POIs verbinden“ holt den Weg zwischen zwei Highlights als Route, zu Fuß, mit dem Rad oder mit dem Auto.',
        'Ein Spot ist ein kleiner POI unter einem Highlight, mit eigenem Text, eigenen Fotos und eigenen Reaktionen. Auf der Karte erscheint er, sobald sein Highlight ausgewählt ist.',
        'Setzt du einen neuen POI nah an einen vorhandenen, fragt die App, ob daraus ein Spot werden soll.',
      ],
    },
    {
      schluessel: 'buch',
      fuer: 'autor',
      titel: 'Buch und Export',
      absaetze: [
        'Auf der Reiseseite baust du den Bericht als Buch, als PDF oder als EPUB. Du wählst, was hineinkommt, und bei Bedarf einen Zeitraum.',
        'Je Mitlesendem gibt es ein eigenes Dokument mit genau diesem Briefwechsel.',
        '„Medien als ZIP“ packt alle Fotos und Videos. Die Dateinamen tragen Datum und Ort.',
        'Ist die Datei fertig, tippst du auf „PDF sichern“, „EPUB sichern“ oder „ZIP sichern“. Am iPhone und iPad öffnet sich das Teilen-Blatt, sonst landet die Datei in „Downloads“.',
      ],
    },
    {
      schluessel: 'verwalten',
      fuer: 'autor',
      titel: 'Die Reise verwalten',
      absaetze: [
        'Auf der Reiseseite schließt du die Reise ab, archivierst sie oder setzt sie zurück. Zurücksetzen nimmt den Inhalt, die Reise bleibt.',
        'Mit „Vorschläge löschen“ räumst du die POIs weg, die beim Import der Route entstanden sind.',
        'Empfänger nimmst du still aus dem Verteiler oder entfernst sie ganz. Still heißt: sie sehen den Bericht weiter so, wie sie ihn verlassen haben.',
      ],
    },

    // ------------------------------------------------------ verwaltung
    {
      schluessel: 'personen',
      fuer: 'verwaltung',
      titel: 'Personen',
      absaetze: [
        'Unter Einstellungen › Administration › Personen schaltest du Konten frei, legst sie still oder löschst sie. Löschen zeigt vorher, was daran hängt.',
        'Dort legst du auch fest, wer Reisen anlegen darf, und gibst einem Konto einen Zweitnamen. Den sieht nur die Verwaltung.',
        'Unter „Zuletzt“ steht je Person, wann sie zuletzt in der App war und wann sie zuletzt etwas geschrieben hat. Wer nur mitliest, hat oft nur die erste Angabe. Die Zeit in der App ist auf etwa eine Stunde genau.',
      ],
    },
    {
      schluessel: 'speicherplatz',
      fuer: 'verwaltung',
      titel: 'Speicherplatz',
      absaetze: [
        'Unter Einstellungen › Administration › Speicherplatz siehst du, wie viel die Fotos und Videos belegen und wie viel noch frei ist.',
        'Der Abgleich sucht Dateien ohne Eintrag und Einträge ohne Datei. Aufräumen entfernt, was nirgends mehr hängt.',
        'Unter „Größe von Medien“ setzt du die Grenzen für Fotos und Videos, die Datenrate beim Verkleinern eines Videos und das Kontingent je Reise. Eine höhere Datenrate gibt ein feineres Bild und eine größere Datei. Passt ein Video dann nicht mehr unter die größte Videodatei, setzt du sie unter Einstellungen › Betrieb › Konfiguration herauf (VIDEO_MAX_BYTES).',
      ],
    },
    {
      schluessel: 'einstellungenFuerAlle',
      fuer: 'verwaltung',
      titel: 'Einstellungen für alle',
      absaetze: [
        'Unter „Farben“ setzt du die Vorgaben für Sprechblasen, Nadeln, Kärtchen und Knöpfe. Sie gelten für jede Reise ohne eigene Farbe.',
        'Unter „POIs auf der Karte“ stellst du ein, ab wann ein Halt in der Route ein Vorschlag wird und wie nah ein POI an einer Route liegen darf.',
        'Dort trägst du auch geschützte Regionen ein. In diesen Gegenden legt der Import keine Vorschläge an.',
        'Unter „Ort und Wetter“ und „GIFs“ siehst du, ob der Zugang zu Apple und zu GIPHY eingerichtet ist.',
      ],
    },

    // --------------------------------------------------------- betrieb
    {
      schluessel: 'betriebKonfiguration',
      fuer: 'betrieb',
      titel: 'Konfiguration',
      absaetze: [
        'Unter Einstellungen › Betrieb › Konfiguration änderst du die Werte der Datei .env aus der App: Absender der E-Mails, Grenzen und die Schlüssel der Dienste.',
        'Eine Änderung gilt sofort. Mit „Letzte Änderung zurücknehmen“ holst du den vorigen Wert zurück.',
        'Datenbank, Cookies und Pfade stehen dort nur zum Lesen. Die änderst du per FTP.',
      ],
    },
    {
      schluessel: 'betriebPruefen',
      fuer: 'betrieb',
      titel: 'Selbstprüfung, Protokoll und Diagnose',
      absaetze: [
        'Die Selbstprüfung zeigt, was dieses Hosting kann: PHP, Datenbank, Schreibrechte und die nötigen Bibliotheken.',
        'Das Protokoll hält jede Änderung mit Konto und Zeitpunkt fest.',
        'Unter Einstellungen › Diagnose stehen die Version, die Netzlage und der Mitschrieb. Schick bei einer Meldung ein Bildschirmfoto davon mit.',
      ],
    },
    {
      schluessel: 'operationsMaintenance',
      fuer: 'betrieb',
      titel: 'Wartungsseite und Datenprüfung',
      absaetze: [
        'Unter Einstellungen › Betrieb › Wartungsseite schaltest du sie ein. Alle anderen sehen dann nur sie, du arbeitest weiter.',
        'Wähle „Alle außer dir“ für eine Migration, „Nur Mitlesende“ für einen Test der neuen Version durch die Autorinnen und Autoren.',
        'Was die anderen ohne Verbindung schreiben, wartet auf ihrem Gerät und geht nach der Wartung hinaus.',
        'Die Reihenfolge bei einem Update: Wartungsseite einschalten, hochladen, Migration einspielen, unter Betrieb › Datenprüfung „Alle prüfen“, die App ansehen, Wartungsseite ausschalten.',
        'Die Wartungsseite schaltet sich nicht von selbst aus. Wenn du nicht mehr hineinkommst, lösche per FTP die Datei maintenance.json im Medienordner.',
      ],
    },
  ] satisfies HandbuchAbschnitt[],

  /**
   * Die Farben: wer stellt ein, was, und wo gilt es.
   *
   * Aus dem Issue: „Zusammenhänge einfach grafisch darstellen (z.B.
   * Farbeinstellungen)." Seit 1.55.0 zeichnet `Farbwege.tsx` genau diese
   * drei Wege; die Zeilen hier stehen darunter weiter als Text, denn eine
   * Zeichnung liest kein Vorleseprogramm.
   */
  farben: {
    titel: 'Farbeinstellungen Chat',
    lead: 'Die Farben werden an 3 Stellen eingestellt. Vorgaben können auf Verwaltungsebene gesetzt werden, der Autor kann diese für eine Reise als Vorgabe für alle Leser einstellen. Jeder Leser kann aber auch die Farben individuell einstellen.',
    bild: 'Drei Wege: du selbst stellst die Farben deiner Reiseliste ein, die Autorin oder der Autor die Sprechblasen einer Reise, die Verwaltung die Vorgaben für alle Reisen ohne eigene Farbe.',
    /**
     * `bildWas` und `bildWo` sind die Kurzformen für die Zeichnung: dort
     * ist ein Kasten 118 Punkte breit, und ein abgeschnittener Satz mit
     * Auslassungspunkten ist keine Auskunft. Der volle Wortlaut steht in
     * `was` und `wo` darunter.
     */
    zeilen: [
      {
        wer: 'Du selbst',
        was: 'Farben der Reiseliste',
        wo: 'Einstellungen › Darstellung. Gilt für dein Konto.',
        bildWas: 'Reiseliste',
        bildWo: 'dein Konto',
      },
      {
        wer: 'Autorin oder Autor',
        was: 'Sprechblasen dieser Reise',
        wo: 'Reiseseite › Sprechblasen. Gilt für alle, die diese Reise lesen.',
        bildWas: 'Sprechblasen',
        bildWo: 'diese Reise',
      },
      {
        wer: 'Verwaltung',
        was: 'Vorgaben für Sprechblasen, Nadeln, Kärtchen und Knöpfe',
        wo: 'Einstellungen › Administration › Farben. Gilt für alle Reisen ohne eigene Farbe.',
        bildWas: 'Vorgaben',
        bildWo: 'alle Reisen',
      },
    ],
    dunkel: 'Im dunklen Modus bleiben eingestellte Farben erhalten und werden nur so weit angepasst, dass die Schrift darauf lesbar ist.',
  },
}
